import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Logo from "../components/Logo";
import GuestSubNav from "../components/GuestSubNav";
import Bagagem from "../components/Bagagem";
import api, { formatApiError } from "../lib/api";
import { IMAGES } from "../lib/content";
import { useContent } from "../lib/ContentContext";
import { TheCast, ShotList, Scenarios } from "./Landing";
import {
  TERMO_TITLE,
  TERMO_INTRO,
  TERMO_CLAUSES,
  TERMO_SIGN_NOTE,
  TERMO_VERSION,
} from "../lib/termo";

const LS_CODE = "alta_guest_code";
const LS_LABEL = "alta_guest_label";

// -------------------------------------------------------
// LOGIN PAGE — cinematographic, full-screen, editorial
// -------------------------------------------------------
function CodeEntry({ onOk }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { content } = useContent();
  const g = content.guest;

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/access/verify", { code: code.trim() });
      localStorage.setItem(LS_CODE, res.data.code);
      localStorage.setItem(LS_LABEL, res.data.label || "");
      toast.success("Acesso liberado");
      onOk(res.data.code, res.data.label || "");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="guest-login"
      className="relative min-h-screen w-full overflow-hidden bg-neutral-900 text-[#F9F8F6]"
    >
      {/* Background image + gradient */}
      <div
        className="absolute inset-0 kenburns"
        style={{
          backgroundImage: `url(${IMAGES.heroAlt})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-black/95" />

      {/* Faint Alta watermark */}
      <div className="absolute inset-y-0 right-0 w-[50%] items-center justify-end pr-8 pointer-events-none hidden lg:flex">
        <Logo variant="light" size={560} testid="login-alta-mark" className="opacity-[0.06]" />
      </div>

      <div className="relative min-h-screen flex flex-col">
        <Nav variant="hero" />

        <div className="flex-1 flex items-center">
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 py-24 fade-up">
            <div className="max-w-[820px]">
              <div className="inline-flex items-center gap-3 border border-[color:var(--alta-red)]/70 bg-[color:var(--alta-red)]/15 px-4 py-2 mono text-[10px] uppercase tracking-[0.28em] backdrop-blur-sm">
                <span className="rec-dot" aria-hidden="true" />
                <span>{g.login_pill_a}</span>
                <span className="text-[#F9F8F6]/50">/</span>
                <span>{g.login_pill_b}</span>
              </div>

              <div className="mt-8 mono text-[11px] uppercase tracking-[0.32em] text-[#F9F8F6]/80">
                {g.login_eyebrow}
              </div>

              <h1
                data-testid="login-title"
                className="mt-5 display text-[#F9F8F6] text-[64px] md:text-[112px] lg:text-[144px] leading-[0.9]"
              >
                {g.login_title_pre}<span className="text-[color:var(--alta-red-soft)]">{g.login_title_accent}</span>
                <span className="text-[color:var(--alta-red)]">.</span>
              </h1>

              <p className="mt-8 serif text-[22px] md:text-[28px] leading-[1.35] text-[#F9F8F6]/85 max-w-[46ch] italic">
                {g.login_body_pre}
                <span className="text-[color:var(--alta-red-soft)]">{g.login_body_accent}</span>
                {g.login_body_post}
              </p>

              <form
                onSubmit={submit}
                className="mt-14 max-w-[640px]"
                data-testid="guest-code-form"
              >
                <div className="mono text-[10px] uppercase tracking-[0.32em] text-[color:var(--alta-red-soft)]">
                  {g.login_field_label}
                </div>
                <div className="mt-4 flex items-end gap-4 border-b-2 border-[#F9F8F6]/40 focus-within:border-[color:var(--alta-red)] transition-colors">
                  <input
                    data-testid="guest-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder={g.login_placeholder}
                    autoComplete="off"
                    autoFocus
                    className="flex-1 bg-transparent serif !text-[42px] md:!text-[56px] tracking-[0.16em] uppercase text-[#F9F8F6] placeholder:text-[#F9F8F6]/25 focus:outline-none py-4"
                  />
                </div>

                <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="guest-code-submit"
                    className="btn-red-primary group"
                  >
                    <span>{loading ? g.login_button_loading : g.login_button}</span>
                    <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </button>
                  <span className="mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6]/50">
                    {g.login_disclaimer}
                  </span>
                </div>
              </form>

              <div className="mt-16 flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6]/45">
                <span className="w-8 h-px bg-[#F9F8F6]/40" />
                <span>{g.login_help}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------
// PERSONALIZED WELCOME HERO (top of logged area)
// -------------------------------------------------------
function WelcomeHero({ label, existing, onConfirmClick }) {
  const { content } = useContent();
  const g = content.guest;
  const rawName = (existing?.full_name || label || "").trim();
  const displayName = rawName || "Convidada";
  const firstName = displayName.split(/\s+/)[0];
  const alreadyConfirmed = !!existing?.consent_accepted;

  return (
    <section
      data-testid="welcome-hero"
      className="relative h-[100vh] min-h-[760px] w-full overflow-hidden bg-neutral-900"
    >
      <div
        className="absolute inset-0 kenburns"
        style={{
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/35" />

      <div className="absolute inset-y-0 right-0 w-[45%] items-center justify-end pr-12 pointer-events-none hidden lg:flex">
        <Logo variant="light" size={520} testid="welcome-alta-mark" className="opacity-[0.08]" />
      </div>

      <div className="absolute inset-0 flex flex-col pt-20">
        <div className="flex-1 flex items-center">
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 fade-up">
            <div className="max-w-[1000px]">
              <div className="inline-flex items-center gap-3 border border-[color:var(--alta-red)]/70 bg-[color:var(--alta-red)]/15 px-4 py-2 mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6] backdrop-blur-sm">
                <span className="rec-dot" aria-hidden="true" />
                <span>{alreadyConfirmed ? g.welcome_pill_confirmada : g.welcome_pill_aguarda}</span>
                <span className="text-[#F9F8F6]/50">/</span>
                <span>{g.welcome_edition_tag}</span>
              </div>

              <div className="mt-8 mono text-[11px] uppercase tracking-[0.32em] text-[#F9F8F6]/80">
                {g.welcome_eyebrow}
              </div>

              <h1
                data-testid="welcome-title"
                className="mt-5 display text-[#F9F8F6] text-[72px] md:text-[132px] lg:text-[176px] leading-[0.88]"
              >
                {g.welcome_greeting}<br />
                <span
                  data-testid="welcome-name"
                  className="text-[color:var(--alta-red-soft)]"
                >
                  {firstName}
                </span>
                <span className="text-[color:var(--alta-red)]">.</span>
              </h1>

              <p className="mt-8 serif text-[22px] md:text-[30px] leading-[1.3] text-[#F9F8F6]/90 max-w-[42ch] italic">
                {g.welcome_body_pre}
                <span className="text-[color:var(--alta-red-soft)]">
                  {g.welcome_body_accent}
                </span>
                {g.welcome_body_post}
              </p>

              <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <button
                  onClick={onConfirmClick}
                  data-testid="welcome-confirm-cta"
                  className="btn-red-primary group"
                >
                  <span>
                    {alreadyConfirmed ? g.welcome_cta_edit : g.welcome_cta_confirm}
                  </span>
                  <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                </button>
                <span className="mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6]/60">
                  {g.welcome_cta_note}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6]/60">
          <span>{g.welcome_scroll_cue}</span>
          <span className="w-px h-8 bg-[#F9F8F6]/50 animate-pulse" />
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------
// INFO STRIP — event essentials
// -------------------------------------------------------
function InfoStrip() {
  const { content } = useContent();
  const g = content.guest;
  const cells = [
    { id: "localizacao", k: g.info_localizacao_label, v: g.info_localizacao_value },
    { id: "checkin", k: g.info_checkin_label, v: g.info_checkin_value },
    { id: "checkout", k: g.info_checkout_label, v: g.info_checkout_value },
    { id: "dress", k: g.info_dress_label, v: g.info_dress_value },
  ];
  return (
    <section
      className="py-16 border-y border-neutral-200 bg-[#F3F1EA]"
      data-testid="info-strip"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-10">
        {cells.map((c) => (
          <div key={c.id} data-testid={`info-${c.id}`}>
            <div className="eyebrow">{c.k}</div>
            <div className="mt-4 serif text-[22px] md:text-[26px] leading-tight text-neutral-900">
              {c.v}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// -------------------------------------------------------
// TIMELINE (schedule)
// -------------------------------------------------------
function Timeline() {
  const { content } = useContent();
  const tl = content.timeline;
  const g = content.guest;
  const days = [1, 2, 3].map((n) => {
    const items = [1, 2, 3, 4, 5, 6]
      .map((i) => ({
        time: tl[`d${n}_i${i}_time`],
        title: tl[`d${n}_i${i}_title`],
        desc: tl[`d${n}_i${i}_desc`],
      }))
      .filter((it) => (it.time || it.title || it.desc)?.toString().trim());
    return {
      day: tl[`d${n}_day`],
      date: tl[`d${n}_date`],
      theme: tl[`d${n}_theme`],
      items,
    };
  });
  return (
    <section className="py-32 md:py-40 bg-[#F9F8F6] border-t border-neutral-200" data-testid="timeline">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="eyebrow">{g.timeline_eyebrow}</div>
          <h2 className="mt-8 serif text-[40px] md:text-[54px] leading-[1] tracking-[-0.01em]">
            {g.timeline_title}
          </h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 space-y-24">
          {days.map((day, idx) => (
            <div key={idx} data-testid={`day-${idx + 1}`}>
              <div className="flex items-baseline justify-between border-b border-neutral-300 pb-4">
                <div className="serif text-[42px] md:text-[56px] leading-none text-neutral-900">
                  {day.day}
                </div>
                <div className="mono text-[11px] uppercase tracking-[0.24em] text-neutral-500">
                  {day.date}
                </div>
              </div>
              <div className="mt-4 mono text-[10px] uppercase tracking-[0.28em] text-[#5A6B52]">
                {day.theme}
              </div>
              <ul className="mt-8 space-y-6">
                {day.items.map((it, i) => (
                  <li key={i} className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-3 md:col-span-2 mono text-[13px] tracking-[0.14em] text-neutral-900">
                      {it.time}
                    </div>
                    <div className="col-span-9 md:col-span-10">
                      <div className="serif text-[20px] md:text-[24px] leading-tight text-neutral-900">
                        {it.title}
                      </div>
                      <div className="mt-1 text-[13px] leading-[1.7] text-neutral-600 font-light">
                        {it.desc}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------
// RSVP FORM
// -------------------------------------------------------
function RSVPForm({ code, existing, onSaved, rsvpRef }) {
  const { content } = useContent();
  const g = content.guest;
  const [form, setForm] = useState({
    full_name: existing?.full_name || "",
    social_handle: existing?.social_handle || "",
    dietary: existing?.dietary || "",
    clothing_size: existing?.clothing_size || "",
    shoe_size: existing?.shoe_size || "",
    signature_name: existing?.signature_name || "",
    consent_accepted: !!existing?.consent_accepted,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({
        full_name: existing.full_name || "",
        social_handle: existing.social_handle || "",
        dietary: existing.dietary || "",
        clothing_size: existing.clothing_size || "",
        shoe_size: existing.shoe_size || "",
        signature_name: existing.signature_name || "",
        consent_accepted: !!existing.consent_accepted,
      });
    }
  }, [existing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_accepted) {
      toast.error("É necessário aceitar o termo de uso de imagem");
      return;
    }
    if (form.signature_name.trim().length < 2) {
      toast.error("Assine com seu nome completo");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/rsvp", {
        code,
        ...form,
        termo_version: TERMO_VERSION,
      });
      toast.success("Presença confirmada. Nos vemos em agosto.");
      onSaved(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="rsvp"
      ref={rsvpRef}
      data-testid="rsvp-form"
      className="relative py-32 md:py-40 bg-[#1A1A1A] text-[#F9F8F6] overflow-hidden scroll-mt-20"
    >
      <div className="absolute -left-24 top-1/2 -translate-y-1/2 opacity-[0.05] pointer-events-none select-none hidden md:block">
        <Logo variant="light" size={620} testid="rsvp-alta-watermark" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="eyebrow !text-[#C7D0BB]">{g.rsvp_eyebrow}</div>
          <h2 className="mt-8 serif text-[40px] md:text-[54px] leading-[1] tracking-[-0.01em]">
            {g.rsvp_title_pre}<br />
            <em className="italic text-[#C7D0BB]">{g.rsvp_title_accent}</em>
          </h2>
          <p className="mt-8 max-w-[42ch] text-[13px] leading-[1.8] text-[#F9F8F6]/70 font-light">
            {g.rsvp_body}
          </p>
          {existing && (
            <div className="mt-10 mono text-[10px] uppercase tracking-[0.28em] text-[#C7D0BB]">
              {g.rsvp_already_note}
            </div>
          )}
        </div>
        <form onSubmit={submit} className="md:col-span-7 md:col-start-6 space-y-10">
          <Field label={g.rsvp_field_name} testid="rsvp-name">
            <input
              data-testid="rsvp-name-input"
              className="field-line !text-[#F9F8F6] !border-[#F9F8F6]/30"
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              required
              minLength={2}
            />
          </Field>
          <Field label={g.rsvp_field_social} testid="rsvp-social">
            <input
              data-testid="rsvp-social-input"
              placeholder={g.rsvp_field_social_placeholder}
              className="field-line !text-[#F9F8F6] !border-[#F9F8F6]/30"
              value={form.social_handle}
              onChange={(e) => set("social_handle", e.target.value)}
              required
            />
          </Field>
          <Field label={g.rsvp_field_diet} testid="rsvp-diet">
            <input
              data-testid="rsvp-diet-input"
              placeholder={g.rsvp_field_diet_placeholder}
              className="field-line !text-[#F9F8F6] !border-[#F9F8F6]/30"
              value={form.dietary}
              onChange={(e) => set("dietary", e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <Field label={g.rsvp_field_clothing} testid="rsvp-clothing">
              <input
                data-testid="rsvp-clothing-input"
                placeholder={g.rsvp_field_clothing_placeholder}
                className="field-line !text-[#F9F8F6] !border-[#F9F8F6]/30"
                value={form.clothing_size}
                onChange={(e) => set("clothing_size", e.target.value)}
                required
              />
            </Field>
            <Field label={g.rsvp_field_shoe} testid="rsvp-shoe">
              <input
                data-testid="rsvp-shoe-input"
                placeholder={g.rsvp_field_shoe_placeholder}
                className="field-line !text-[#F9F8F6] !border-[#F9F8F6]/30"
                value={form.shoe_size}
                onChange={(e) => set("shoe_size", e.target.value)}
                required
              />
            </Field>
          </div>

          {/* TERMO DE USO DE IMAGEM */}
          <div
            data-testid="termo-block"
            className="mt-4 border border-[#F9F8F6]/20 bg-[#F9F8F6]/[0.03] p-6 md:p-8"
          >
            <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red-soft)]">
              <span className="rec-dot" aria-hidden="true" />
              <span>{g.rsvp_termo_pill}</span>
              <span className="text-[#F9F8F6]/40">/</span>
              <span>{TERMO_VERSION}</span>
            </div>
            <h3 className="mt-5 serif text-[26px] md:text-[32px] leading-tight text-[#F9F8F6]">
              {TERMO_TITLE}
            </h3>
            <div className="mt-2 mono text-[10px] uppercase tracking-[0.24em] text-[#F9F8F6]/60">
              {TERMO_INTRO}
            </div>

            <div className="mt-6 max-h-[320px] overflow-y-auto pr-3 space-y-5 border-t border-[#F9F8F6]/15 pt-5" data-testid="termo-scroll">
              {TERMO_CLAUSES.map((c) => (
                <div key={c.n} className="grid grid-cols-12 gap-3">
                  <div className="col-span-2 md:col-span-1 mono text-[10px] text-[color:var(--alta-red-soft)] tracking-[0.24em]">
                    {c.n}
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <div className="mono text-[10px] uppercase tracking-[0.24em] text-[#F9F8F6]">
                      {c.title}
                    </div>
                    <p className="mt-1 text-[13px] leading-[1.7] text-[#F9F8F6]/75 font-light">
                      {c.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-[11px] leading-[1.7] text-[#F9F8F6]/60 border-t border-[#F9F8F6]/15 pt-5">
              {TERMO_SIGN_NOTE}
            </p>

            <div className="mt-6 grid grid-cols-1 gap-5">
              <label className="block" data-testid="termo-signature">
                <span className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red-soft)]">
                  {g.rsvp_signature_label}
                </span>
                <input
                  data-testid="termo-signature-input"
                  value={form.signature_name}
                  onChange={(e) => set("signature_name", e.target.value)}
                  placeholder={g.rsvp_signature_placeholder}
                  className="field-line mt-3 !text-[#F9F8F6] !border-[#F9F8F6]/40 serif !text-[22px] italic"
                  autoComplete="off"
                  required
                  minLength={2}
                />
              </label>

              <label
                className="flex items-start gap-4 cursor-pointer group"
                data-testid="termo-accept"
              >
                <input
                  data-testid="termo-accept-input"
                  type="checkbox"
                  checked={form.consent_accepted}
                  onChange={(e) => set("consent_accepted", e.target.checked)}
                  className="mt-1 h-5 w-5 accent-[color:var(--alta-red)] cursor-pointer flex-shrink-0"
                  required
                />
                <span className="text-[13px] leading-[1.6] text-[#F9F8F6]/85 group-hover:text-[#F9F8F6] transition-colors">
                  {g.rsvp_consent_pre}
                  <em className="italic text-[color:var(--alta-red-soft)]">{g.rsvp_consent_accent}</em>
                  {g.rsvp_consent_post}
                </span>
              </label>
            </div>
          </div>
          <div className="pt-6">
            <button
              type="submit"
              disabled={loading || !form.consent_accepted}
              data-testid="rsvp-submit"
              className="btn-sharp !bg-[#F9F8F6] !text-neutral-900 !border-[#F9F8F6] hover:!bg-[color:var(--alta-red)] hover:!border-[color:var(--alta-red)] hover:!text-[#F9F8F6] disabled:!bg-[#F9F8F6]/40 disabled:!text-neutral-500 disabled:hover:!bg-[#F9F8F6]/40 disabled:hover:!text-neutral-500"
            >
              {loading ? g.rsvp_submit_loading : existing ? g.rsvp_submit_edit : g.rsvp_submit_new}
            </button>
            {!form.consent_accepted && (
              <div className="mt-3 mono text-[10px] uppercase tracking-[0.24em] text-[#F9F8F6]/50">
                {g.rsvp_submit_hint}
              </div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="mono text-[10px] uppercase tracking-[0.28em] text-[#C7D0BB]">
        {label}
      </span>
      <div className="mt-3">{children}</div>
    </label>
  );
}

function ClosingImage() {
  const { content } = useContent();
  const g = content.guest;
  return (
    <section data-testid="closing" className="relative h-[70vh] overflow-hidden">
      <img
        src={IMAGES.heroAlt}
        alt="Shambala House"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-12 flex items-end pb-24">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.32em] text-[#F9F8F6]/70">
            {g.closing_eyebrow}
          </div>
          <div className="mt-6 serif text-[#F9F8F6] text-[44px] md:text-[72px] leading-[1] max-w-[16ch]">
            {g.closing_line_pre}<em className="italic text-[color:var(--alta-red-soft)]">{g.closing_line_accent}</em>.
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------
// MAIN
// -------------------------------------------------------
export default function GuestPanel() {
  const { content } = useContent();
  const g = content.guest;
  const [code, setCode] = useState(localStorage.getItem(LS_CODE) || "");
  const [label, setLabel] = useState(localStorage.getItem(LS_LABEL) || "");
  const [existing, setExisting] = useState(null);
  const rsvpRef = useRef(null);

  // Fetch RSVP once code is known
  useEffect(() => {
    if (!code) return;
    api.get(`/rsvp/${code}`).then((r) => setExisting(r.data)).catch(() => setExisting(null));
  }, [code]);

  const logout = () => {
    localStorage.removeItem(LS_CODE);
    localStorage.removeItem(LS_LABEL);
    setCode("");
    setLabel("");
    setExisting(null);
    toast.success("Sessão encerrada");
  };

  const scrollToRSVP = () => {
    rsvpRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleLoggedIn = (c, l) => {
    setCode(c);
    setLabel(l);
  };

  if (!code) return <CodeEntry onOk={handleLoggedIn} />;

  const displayName = (existing?.full_name || label || "").trim().split(/\s+/)[0] || "Convidada";

  return (
    <div data-testid="guest-panel" className="bg-[#F9F8F6]">
      <Nav
        variant="guest"
        guestName={displayName}
        badgeLabel={g.nav_badge}
        logoutLabel={g.nav_logout}
        confirmLabel={g.nav_confirm_short}
        onLogout={logout}
        onConfirm={scrollToRSVP}
      />
      <GuestSubNav onConfirm={scrollToRSVP} />
      <div id="start">
        <WelcomeHero label={label} existing={existing} onConfirmClick={scrollToRSVP} />
      </div>
      <InfoStrip />
      <div id="cenarios" className="scroll-mt-32">
        <Scenarios />
      </div>
      <div id="elenco" className="scroll-mt-32">
        <TheCast />
      </div>
      <div id="cortes" className="scroll-mt-32">
        <ShotList />
      </div>
      <div id="bagagem" className="scroll-mt-32">
        <Bagagem />
      </div>
      <div id="agenda" className="scroll-mt-32">
        <Timeline />
      </div>
      <RSVPForm code={code} existing={existing} onSaved={setExisting} rsvpRef={rsvpRef} />
      <ClosingImage />
      <div className="bg-[#1A1A1A] text-[#F9F8F6]/70 py-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between mono text-[10px] uppercase tracking-[0.28em]">
          <div className="flex items-center gap-4">
            <Logo variant="light" size={22} testid="guest-footer-alta-logo" />
            <span>{g.footer_line}</span>
          </div>
          <button
            data-testid="guest-logout-footer"
            onClick={logout}
            className="hover:text-[#F9F8F6] transition-colors"
          >
            {g.footer_logout}
          </button>
        </div>
      </div>
    </div>
  );
}
